import React, { useState, useCallback } from 'react';
import { IntlShape } from 'react-intl';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { FieldProps } from 'formik';
import { SingleDatePicker } from 'react-dates';
import moment from 'moment';
import { ReactComponent as IconCalendar } from '@whitelabel/media/icons/small/calendar.svg';
import { locales } from '@whitelabel/helpers/site';
import { StyledSingleDatePickerInput } from './styled';

export interface ISingleDatePickerProps {
  id: string;
  className?: string;
  name: string;
  value: string;
  placeholder?: string;
  format?: string;
  minDate?: string;
  maxDate?: string;
  onChange: (...args: any[]) => any;
  onBlur: (...args: any[]) => any;
  form: FieldProps['form'];
  intl: IntlShape;
  displayFormat?: string;
  keepTime?: boolean;
  invalid: boolean;
}

const SingleDatePickerInput = ({
  id,
  name,
  value,
  className,
  placeholder = 'DD/MM/YYYY',
  format,
  minDate,
  maxDate,
  onChange,
  onBlur,
  form: { setFieldTouched, setFieldValue },
  intl,
  displayFormat = 'DD/MM/YYYY',
  keepTime = false,
  invalid,
  ...props
}: ISingleDatePickerProps): JSX.Element => {
  const [focused, setFocused] = useState(false);

  let dateString = value;
  let time = '';
  if (keepTime) {
    dateString = value.substring(0, 10);
    time = value.substring(10);
  }

  const onFocusChange = useCallback(
    ({ focused: isFocused }) => {
      setFocused(isFocused);
      if (!isFocused && onBlur) onBlur(value);
    },
    [onBlur, value],
  );

  const onDateChange = useCallback(
    async (date) => {
      if (date) {
        let newValue: string;
        if (keepTime) {
          newValue = date.format('YYYY-MM-DD') + time;
        } else {
          newValue = date instanceof moment ? (date as moment.Moment).startOf('day').format(format) : '';
        }
        await setFieldValue(name, newValue);
        if (onChange) onChange(newValue);
      } else {
        await setFieldValue(name, '');
      }
      setFieldTouched(name, true);
    },
    [format, name, onChange, setFieldTouched, setFieldValue, keepTime, time],
  );

  const isOutsideRange = useCallback(
    (date) => {
      if (minDate && maxDate) {
        return !date.isBetween(minDate, maxDate, null, '[]');
      }

      if (minDate) return date.isBefore(minDate);
      if (maxDate) return date.isAfter(maxDate);
      return false;
    },
    [minDate, maxDate],
  );
  // @ts-ignore
  const isRTL = locales[intl.locale].isRTL;
  return (
    <StyledSingleDatePickerInput $rtl={isRTL} className={className} $focused={focused} $invalid={invalid}>
      <SingleDatePicker
        id={id}
        date={dateString ? moment.utc(dateString, format) : null}
        placeholder={placeholder}
        focused={focused}
        onDateChange={onDateChange}
        onFocusChange={onFocusChange}
        isOutsideRange={isOutsideRange}
        numberOfMonths={1}
        isRTL={isRTL}
        hideKeyboardShortcutsPanel
        showDefaultInputIcon
        displayFormat={displayFormat}
        readOnly
        customInputIcon={<IconCalendar />}
        {...props}
      />
    </StyledSingleDatePickerInput>
  );
};

export default SingleDatePickerInput;
